/* @flow */
import gql from 'graphql-tag';
import * as React from 'react';
import { graphql } from 'react-apollo';

import { NavItem, GroupedNavItem } from 'components/NavItem';
import EventTracking from 'lib/EventTracking';
import View from 'components/View.jsx';

import './styles/AdminSettingsLayout.scss';

type Props = {
  children: *,
  organization: ?Object,
  orgId: number,
  location: Object,
  params: {
    groupCode: string,
  },
};

function AdminSettingsLayout(props: Props) {
  const { children, organization, location, orgId } = props;

  if (!organization) {
    return null;
  }

  const { type, permissions } = organization;
  const adminConsolePermissions = permissions.adminConsole;
  const isDistrict = type === 'District';
  const stub = `/classes/${props.params.groupCode}/settings/organization/`;
  const path = location.pathname.match(/\w+$/)[0];

  EventTracking.view('admin_console_settings', {
    uiContext: isDistrict ? 'district' : 'school',
    component: path === 'billing' ? 'billing' : `${path}_administrators`,
  });

  return (
    <div className="AdminSettingsLayout">
      <nav className="AdminSettingsLayout-nav">
        <ul className="AdminSettingsLayout-nav-list">
          {adminConsolePermissions.seeSettingsTabDistrictAdmins && (
            <NavItem to={`${stub}administrators/district`}>District administrators</NavItem>
          )}
          {adminConsolePermissions.seeSettingsTabSchoolAdmins && (
            <NavItem to={`${stub}administrators/school`}>School administrators</NavItem>
          )}
          {adminConsolePermissions.seeSettingsTabBilling && (
            <NavItem to={`${stub}billing`}>Billing</NavItem>
          )}
          {adminConsolePermissions.seeSettingsTabSyncOverview && (
            <NavItem to={`${stub}sis_sync`}>SIS sync</NavItem>
          )}
          {adminConsolePermissions.seeConnectedAccountsTab && (
            <NavItem to={`${stub}integrated_tools`}>Integrated Apps</NavItem>
          )}
          {adminConsolePermissions.seeSettingsTabAutoMessaging && (
            <GroupedNavItem
              name="Auto Messaging"
              items={[
                <NavItem to={`${stub}auto_messages`}>Auto messages</NavItem>,
                <NavItem to={`${stub}uploaded_files`}>Uploaded files</NavItem>,
                <NavItem to={`${stub}sftp_credentials`}>SFTP credentials</NavItem>,
              ]}
              collapsible
            />
          )}
        </ul>
      </nav>
      <View className="AdminSettingsLayout-content" scroll>
        {React.cloneElement(children, {
          orgId,
          orgType: type,
          groupCode: props.params.groupCode,
          organizationUUID: organization.uuid,
        })}
      </View>
    </div>
  );
}

const withOrganization = graphql(
  gql`
    query AdminSettingsLayout($organizationId: Int) {
      organizations(ids: [$organizationId]) {
        uuid
        type
        permissions {
          adminConsole {
            seeSettingsTabBilling
            seeConnectedAccountsTab
            seeSettingsTabSchoolAdmins
            seeSettingsTabDistrictAdmins
            seeSettingsTabSyncOverview
            seeSettingsTabAutoMessaging
          }
        }
      }
    }
  `,
  {
    options: ({ orgId }) => ({
      variables: { organizationId: orgId },
    }),
    props: ({ data }) => ({
      organization: data.organizations ? data.organizations[0] : null,
    }),
  }
);

export { AdminSettingsLayout };
export default withOrganization(AdminSettingsLayout);
